import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import APAccordion from "../../../components/APAccordion";
import AIButton from "../../../components/buttons/APOutlineButtons/AIButton";
import { ExpandCollapseAllButton } from "../../../components/buttons/ExpandCollapseAllButton";
import InfoButton from "../../../components/buttons/InfoButton";
import VehicleImageCarousel from "../../../components/carousels/VehicleImageCarousel";
import PoweredByGemini from "../../../components/PoweredByGemini";
import { useExpandCollapseAll } from "../../../hooks/useExpandCollapseAll";
import VehicleStats from "../../../quote/components/VehicleStats";
import { FaqProps } from "../../../types/Faq";
import { RedbookVehiclePhoto, VehicleReview } from "../../../types/Vehicles";
import { VehicleVariantSummarisedSpecs } from "../../vehicleSearch/components/VehicleVariantSummarisedSpecs";

type VehicleReviewProps = {
  review: VehicleReview;
  vehicleImages: RedbookVehiclePhoto[];
  displaySuggestionsButton: boolean;
  shouldPromptForAIConsent: boolean;
  onVehicleSuggestions: () => void;
  faqProps: FaqProps;
};

const APVehicleReview = ({
  review,
  vehicleImages,
  displaySuggestionsButton,
  shouldPromptForAIConsent,
  onVehicleSuggestions,
  faqProps,
}: VehicleReviewProps) => {
  const expandCollapseAll = useExpandCollapseAll("allExpanded");

  return (
    <Box>
      <Box mb={1} sx={{ display: "flex", justifyContent: "space-between" }}>
        {faqProps.renderAppLocation(
          "AIVehicleReview-PoweredByAI",
          <PoweredByGemini />,
          false,
          true
        )}
        <Box mr={1}>
          {faqProps.renderAppLocation(
            "AIVehicleReview-Page",
            <InfoButton />,
            true,
            true
          )}
        </Box>
      </Box>
      <Stack spacing={3}>
        <Box>
          <APAccordion
            title={`${review.variant.year} ${review.variant.make} ${
              review.variant.model
            } ${review.variant.variant ?? ""}`}
            hideExpand={true}
            alwaysExpanded={true}
            smallHeader={true}
          >
            {vehicleImages.length > 0 && (
              <Box pt={2}>
                <VehicleImageCarousel images={vehicleImages} />
              </Box>
            )}
          </APAccordion>
        </Box>

        <Box>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <ExpandCollapseAllButton controller={expandCollapseAll} />
          </Box>
          <VehicleStats
            value={review.value}
            comfort={review.comfort}
            performance={review.performance}
            costToRun={review.costToRun}
            statDetails={{
              value: review.valueText,
              comfort: review.comfortText,
              performance: review.performanceText,
              costToRun: review.costToRunText,
            }}
            forceOpen={expandCollapseAll.isAllExpanded}
            forceClose={expandCollapseAll.isAllCollapsed}
            grid={true}
            faqProps={faqProps}
            faqs={{
              value: "AIVehicleReview-ValueReview",
              comfort: "AIVehicleReview-ComfortReview",
              performance: "AIVehicleReview-PerformanceReview",
              costToRun: "AIVehicleReview-RunningCostReview",
            }}
          />
        </Box>

        <Stack
          sx={{
            display: "grid",
            gridTemplateColumns: { md: "1fr 1fr" },
            gridTemplateAreas: {
              md: '"specs review" "specs pros" "specs cons"',
              xs: '"review" "pros" "cons" "specs"',
            },
            gap: { md: 1, xs: 2 },
          }}
        >
          <Box sx={{ gridArea: "review" }}>
            {faqProps.renderAppLocation(
              "AIVehicleReview-Review",
              <Typography variant="bodyLarge">{review.comparison}</Typography>,
              false
            )}
          </Box>

          <Box sx={{ gridArea: "pros" }}>
            {faqProps.renderAppLocation(
              "AIVehicleReview-Review",
              <>
                <Typography
                  variant="bodyLarge"
                  sx={{ fontWeight: "600", display: "inline" }}
                >
                  Pros:{" "}
                </Typography>
                <Typography variant="bodyLarge" display="inline">
                  {review.pros}
                </Typography>
              </>,
              false
            )}
          </Box>

          <Box sx={{ gridArea: "cons" }}>
            {faqProps.renderAppLocation(
              "AIVehicleReview-Review",
              <>
                <Typography
                  variant="bodyLarge"
                  sx={{ fontWeight: "600", display: "inline" }}
                >
                  Cons:{" "}
                </Typography>

                <Typography variant="bodyLarge" display="inline">
                  {review.cons}
                </Typography>
              </>,
              false
            )}
          </Box>

          <Box sx={{ gridArea: "specs" }}>
            <VehicleVariantSummarisedSpecs
              vehicleVariant={review.variant}
              includeRows={["money", "car", "speedometer", "range"]}
            />
          </Box>
        </Stack>

        {displaySuggestionsButton && (
          <Box>
            <AIButton
              type="vehicle-suggestions"
              onClick={() => onVehicleSuggestions()}
              shouldPromptForAIConsent={shouldPromptForAIConsent}
              faqProps={faqProps}
              faqLocationIdentifier="AIVehicleReview-AIVehicleSuggestions"
            />
          </Box>
        )}
      </Stack>
    </Box>
  );
};

export default APVehicleReview;
